import React, { useCallback, useEffect, useState } from 'react';
import { Tappable, Textarea, Tooltip } from '@vectord/components';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import cogoToast from 'cogo-toast';

import {
  RECORDING_REQUEST_STATUSES,
  RECORDING_REQUEST_MODAL_STAGE,
  RecordingRequestIdSelector,
  setModalStage,
  useUpdateRecordingRequestMutation,
} from 'features/recording';
import Field from 'components/Field';
import styles from './RecordingRequestActionModal.module.scss';

const RecordingRequestActionModal = () => {
  const dispatch = useDispatch();
  const [note, setNote] = useState('');
  const requestId = useSelector(RecordingRequestIdSelector);
  const [updateRecordingRequest, { isSuccess, error, isLoading: isUpdating }] =
    useUpdateRecordingRequestMutation();
  const disableRejectAction = isUpdating || note.trim().length === 0;
  const disableTooltip = !(note.trim().length === 0);

  const handleRequestAction = useCallback(
    (action) => {
      updateRecordingRequest({ id: requestId, note, action });
    },
    [note, requestId, updateRecordingRequest]
  );

  const handleNoteChange = useCallback((event) => {
    setNote(event.target.value);
  }, []);

  const handleAcceptClick = useCallback(() => {
    handleRequestAction(RECORDING_REQUEST_STATUSES.accepted);
  }, [handleRequestAction]);

  const handleRejectClick = useCallback(() => {
    handleRequestAction(RECORDING_REQUEST_STATUSES.rejected);
  }, [handleRequestAction]);

  useEffect(() => {
    if (isSuccess) {
      dispatch(setModalStage(RECORDING_REQUEST_MODAL_STAGE.notOpen));
      cogoToast.success('Updated recording request successfully!');
    }
  }, [dispatch, isSuccess]);

  useEffect(() => {
    if (error) {
      dispatch(setModalStage(RECORDING_REQUEST_MODAL_STAGE.notOpen));
      cogoToast.error(
        error?.data?.message ||
          'There was some problem in carrying out this action. Please try again later!'
      );
    }
  }, [dispatch, error]);

  return (
    <div className={styles.root}>
      <div className="bold h3 m-b-10 dark">Request Action</div>
      <hr />
      <Field
        name="rejection_note"
        label="Enter a note"
        labelClassName="hint normal"
        className="h5 m-t-20 p-t-5 m-b-10 p-h-10"
      >
        <Textarea
          value={note}
          className={styles.noteInput}
          onChange={handleNoteChange}
        />
      </Field>
      <div className={styles.actionButtonColumn}>
        <Tappable
          className={classNames('btn btn-primary', styles.actionButton)}
          onClick={handleAcceptClick}
        >
          Accept
        </Tappable>
        <Tooltip
          isDisabled={disableTooltip}
          className={styles.actionButton}
          title="Enter a note before performing this action"
        >
          <Tappable
            className={classNames('btn btn-danger', styles.actionButtonReject)}
            disabled={disableRejectAction}
            onClick={handleRejectClick}
          >
            Reject
          </Tappable>
        </Tooltip>
      </div>
    </div>
  );
};

export default RecordingRequestActionModal;
